<script setup lang="ts">
import { reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import validators from '@/utils/validators.ts';
import UserService from '@/services/user/userService.ts';
import { useSnackbarStore } from "@/stores/snackbarStore.ts";

import type { ValidationRule } from '@/types/utils/validators.type.ts';
import type { CreateUserRequest } from '@/types/services/user/user.type.ts';

const emit = defineEmits<{
  (emit: 'created'): void
}>()

const { t } = useI18n()
const snackbarStore = useSnackbarStore()

const loading = ref<boolean>(false)

//--- Create user form ---//
const formValid = ref<boolean>(false)
const emailRules = ref<ValidationRule[]>([...validators.emailRules])
const firstNameRules = ref<ValidationRule[]>([...validators.firstNameRules])
const lastNameRules = ref<ValidationRule[]>([...validators.lastNameRules])
const form = reactive({
  firstName: '',
  lastName: '',
  email: '',
  role: 'dam',
})

async function createDam(): Promise<void> {
  loading.value = true

  const newUser: CreateUserRequest = {
    firstName: form.firstName,
    lastName: form.lastName,
    email: form.email,
  }

  try {
    await UserService.createDam(newUser);

    snackbarStore.openSnackbar({
      text: t("views.createDam.emailSent"),
      color: "success"
    })
  } finally {
    loading.value = false
    emit('created')
  }
}
</script>

<template>
  <!-- User creation form -->
  <v-row>
    <v-col cols="12">
      <v-form v-model="formValid" @submit.prevent="createDam">
        <v-row>
          <v-col cols="12" class="d-flex align-center">
            <v-icon class="mr-2" color="primary" icon="PhInfo" />
            <h2 class="subtitle">{{ t("views.createDam.subtitle") }}</h2>
          </v-col>

          <v-col cols="6">
            <v-text-field
              v-model="form.firstName"
              :label="t('common.inputs.firstName.label')"
              :placeholder="t('common.inputs.firstName.placeholder')"
              :rules="firstNameRules"
              autocomplete="given-name"
              hide-details="auto"
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model="form.lastName"
              :label="t('common.inputs.lastName.label')"
              :placeholder="t('common.inputs.lastName.placeholder')"
              :rules="lastNameRules"
              autocomplete="family-name"
              hide-details="auto"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="form.email"
              :label="t('common.inputs.email.label')"
              :placeholder="t('common.inputs.email.placeholder')"
              :rules="emailRules"
              autocomplete="email"
              hide-details="auto"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-text-field
              :model-value="t(`common.role.${form.role}`)"
              :label="t('common.inputs.role.label')"
              autocomplete="off"
              hide-details="auto"
              disabled
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" class="d-flex justify-end">
            <v-btn
              :disabled="!formValid || loading"
              :loading="loading"
              :text="t('common.buttons.create')"
              class="w-100 w-sm-auto"
              type="submit"
            >
              <template #prepend>
                <v-icon icon="PhUserPlus"/>
              </template>
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-col>
  </v-row>
</template>
